import { useEffect, useState, useCallback } from "react";
import { useAuth } from "@/context/AuthContext";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";
import {
  Receipt, AlertTriangle, Mail, Printer, Send, CheckCircle2, Clock,
  FileText, MessageSquare, ChevronLeft, Sparkles, TestTube, DollarSign,
} from "lucide-react";

const AGING_TONE = {
  "current": "bg-primary/15 text-gold",
  "30-60":   "bg-[hsl(32_87%_62%/0.18)] text-[hsl(32_60%_35%)]",
  "60-90":   "bg-secondary/20 text-secondary",
  "90+":     "bg-destructive/15 text-destructive",
};

const STAGE_META = [
  { id: "friendly_reminder", label: "1st Notice · Friendly Reminder",  days: 15 },
  { id: "second_notice",     label: "2nd Notice · Registered",         days: 15 },
  { id: "demand_letter",     label: "3rd Notice · 10-Day Demand",      days: 10 },
  { id: "intent_to_lien",    label: "Final Notice · Intent to Lien",   days: 30 },
];

export default function Delinquency() {
  const { user } = useAuth();
  const isBoard = user?.role === "admin" || user?.role === "board";
  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState({ total_owed: 0, delinquent_count: 0 });
  const [selected, setSelected] = useState(null); // owner_id
  const [detail, setDetail] = useState(null);
  const [seeding, setSeeding] = useState(false);
  const [noticeOpen, setNoticeOpen] = useState(false);
  const [noticeForm, setNoticeForm] = useState({ stage: "friendly_reminder", late_fee: 0, method: "email", notes: "" });
  const [sending, setSending] = useState(false);
  const [lastNotice, setLastNotice] = useState(null);
  const [nextRec, setNextRec] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/delinquency");
      setRows(data.rows);
      setSummary({ total_owed: data.total_owed, delinquent_count: data.delinquent_count });
    } catch (err) {
      console.error("Delinquency load failed:", err);
      toast.error(err.response?.data?.detail || "Could not load");
    }
  }, []);

  const openDetail = async (owner_id) => {
    setSelected(owner_id);
    try {
      const [d, n] = await Promise.all([
        api.get(`/delinquency/${owner_id}`),
        api.get(`/delinquency/${owner_id}/next-notice`),
      ]);
      setDetail(d.data);
      setNextRec(n.data);
      // Prefill notice modal with policy recommendation
      if (n.data.needed) {
        setNoticeForm((f) => ({ ...f, stage: n.data.stage, late_fee: n.data.late_fee }));
      }
    } catch (err) {
      console.error("delinquency detail failed:", err);
      toast.error("Could not load owner detail");
    }
  };

  useEffect(() => { load(); }, [load]);

  const seedSample = async () => {
    setSeeding(true);
    try {
      await api.post("/demo/seed-delinquency");
      toast.success("Sample delinquency data loaded");
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not seed");
    } finally { setSeeding(false); }
  };

  const clearSample = async () => {
    setSeeding(true);
    try {
      await api.delete("/demo/sample-data");
      toast.success("Sample data cleared");
      setSelected(null); setDetail(null);
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not clear");
    } finally { setSeeding(false); }
  };

  const sendNotice = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      const { data } = await api.post(`/delinquency/${selected}/notice`, {
        ...noticeForm,
        late_fee: Number(noticeForm.late_fee) || 0,
      });
      setLastNotice(data);
      toast.success(`Notice sent · ${data.stage_label}`);
      setNoticeOpen(false);
      await openDetail(selected);
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not send notice");
    } finally { setSending(false); }
  };

  const completeTask = async (nid) => {
    try {
      await api.post(`/delinquency/notices/${nid}/complete`);
      toast.success("Task marked complete");
      await openDetail(selected);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not update");
    }
  };

  const openPrint = (nid) => {
    const base = process.env.REACT_APP_BACKEND_URL;
    window.open(`${base}/api/delinquency/notices/${nid}/print`, "_blank", "noopener");
  };

  if (!isBoard) {
    return (
      <div className="hoa-card p-10 text-center" data-testid="delinquency-forbidden">
        <AlertTriangle className="w-8 h-8 text-muted-foreground mx-auto mb-3" />
        <div className="text-lg font-semibold">Board-only view</div>
        <p className="text-sm text-muted-foreground mt-1">Sign in as a Board Director to open the delinquency dashboard.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="delinquency-page">
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <div className="flex items-center gap-2 text-secondary text-sm font-medium mb-2">
            <Receipt className="w-4 h-4" /> Collections cockpit
          </div>
          <h1 className="text-3xl font-semibold tracking-tight">Outstanding dues</h1>
          <p className="text-foreground/70 mt-1">Every delinquent owner in one view — with the aging clock, notice history, and one-click escalations.</p>
        </div>
        <div className="flex gap-2">
          <Link to="/collections-policy">
            <Button variant="outline" className="rounded-full" data-testid="manage-policy-btn">
              <FileText className="w-4 h-4 mr-1.5" /> Collections policy
            </Button>
          </Link>
          <Button
            variant="outline"
            className="rounded-full"
            onClick={seedSample}
            disabled={seeding}
            data-testid="seed-delinquency-btn"
          >
            <TestTube className="w-4 h-4 mr-1.5" /> Load sample data
          </Button>
          <Button
            variant="ghost"
            className="rounded-full text-muted-foreground"
            onClick={clearSample}
            disabled={seeding}
            data-testid="clear-sample-btn"
          >
            Clear samples
          </Button>
        </div>
      </div>

      {/* Summary */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="hoa-card royal-ring p-6" data-testid="delinq-total">
          <div className="text-xs uppercase tracking-wider text-muted-foreground">Total owed</div>
          <div className="text-4xl font-semibold text-gold font-mono mt-2">${summary.total_owed.toFixed(2)}</div>
        </div>
        <div className="hoa-card p-6">
          <div className="text-xs uppercase tracking-wider text-muted-foreground">Delinquent units</div>
          <div className="text-4xl font-semibold text-foreground font-mono mt-2">{summary.delinquent_count}</div>
        </div>
        <div className="hoa-card p-6">
          <div className="text-xs uppercase tracking-wider text-muted-foreground">In legal escalation (60+ days)</div>
          <div className="text-4xl font-semibold text-destructive font-mono mt-2">
            {rows.filter((r) => r.days_past_due >= 60).length}
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="hoa-card overflow-hidden">
        <div className="grid grid-cols-12 gap-3 px-5 py-3 text-xs uppercase tracking-wider text-muted-foreground border-b border-border">
          <div className="col-span-3">Owner · Unit</div>
          <div className="col-span-2 text-right">Balance</div>
          <div className="col-span-2">Aging</div>
          <div className="col-span-2">Last notice</div>
          <div className="col-span-3 text-right">Actions</div>
        </div>
        {rows.length === 0 ? (
          <div className="p-10 text-center text-muted-foreground" data-testid="delinq-empty">
            No delinquent accounts. Click <span className="text-gold">Load sample data</span> to try the flow.
          </div>
        ) : rows.map((r) => (
          <button
            key={r.owner_id}
            type="button"
            onClick={() => openDetail(r.owner_id)}
            className="grid grid-cols-12 gap-3 px-5 py-4 border-b border-border/50 items-center w-full text-left hover:bg-accent/40 focus:bg-accent/40 focus:outline-none transition-colors"
            data-testid={`delinq-row-${r.owner_id}`}
          >
            <div className="col-span-3">
              <div className="font-semibold text-gold underline decoration-dotted underline-offset-4" data-testid={`owner-name-${r.owner_id}`}>{r.owner_name}</div>
              <div className="text-xs text-muted-foreground">Unit {r.unit_number || "—"} · {r.owner_email}</div>
            </div>
            <div className="col-span-2 text-right font-mono text-destructive font-semibold" data-testid={`owner-balance-${r.owner_id}`}>${r.balance.toFixed(2)}</div>
            <div className="col-span-2">
              <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${AGING_TONE[r.aging_bucket] || AGING_TONE.current}`}>
                {r.days_past_due}d · {r.aging_bucket}
              </span>
            </div>
            <div className="col-span-2 text-xs text-muted-foreground">
              {r.last_notice_at ? (
                <>
                  <div>{r.last_notice_stage?.replace(/_/g, " ")}</div>
                  <div>{new Date(r.last_notice_at).toLocaleDateString()}</div>
                </>
              ) : "—"}
            </div>
            <div className="col-span-3 flex justify-end gap-2">
              <span className="rounded-full text-xs bg-gold/15 text-gold px-3 py-1 font-medium" data-testid={`open-delinq-${r.owner_id}`}>
                Open account →
              </span>
            </div>
          </button>
        ))}
      </div>

      {/* Detail drawer */}
      {selected && detail && (
        <div className="hoa-card royal-ring p-6" data-testid="delinq-detail">
          <div className="flex items-start justify-between mb-4">
            <div>
              <button className="text-sm text-muted-foreground hover:text-foreground flex items-center gap-1 mb-2" onClick={() => { setSelected(null); setDetail(null); }} data-testid="close-detail-btn">
                <ChevronLeft className="w-4 h-4" /> Back to list
              </button>

          {nextRec && nextRec.needed && (
            <div className="hoa-card royal-ring p-4 mb-4 flex items-start gap-3" data-testid="next-recommended-banner">
              <Sparkles className="w-5 h-5 text-gold flex-shrink-0 mt-0.5" />
              <div className="flex-1 text-sm">
                <div className="font-semibold text-gold">Policy recommends: {STAGE_META.find((s) => s.id === nextRec.stage)?.label || nextRec.stage} (seq {nextRec.seq})</div>
                <div className="text-foreground/80">{nextRec.reason} · cure window {nextRec.cure_days}d · late fee ${nextRec.late_fee}</div>
              </div>
            </div>
          )}

              <h2 className="text-2xl font-semibold">{detail.owner.name}</h2>
              <div className="text-sm text-muted-foreground">Unit {detail.owner.unit_number || "—"} · {detail.owner.email}</div>
            </div>
            <div className="text-right">
              <div className="text-xs uppercase tracking-wider text-muted-foreground">Balance owed</div>
              <div className="text-3xl font-semibold text-gold font-mono">${detail.summary.balance.toFixed(2)}</div>
              <div className="text-xs text-muted-foreground mt-1">{detail.summary.days_past_due} days past due</div>
            </div>
          </div>

          <Dialog open={noticeOpen} onOpenChange={setNoticeOpen}>
            <DialogTrigger asChild>
              <Button className="rounded-full bg-gold text-secondary-foreground hover:brightness-110 mb-4" data-testid="send-notice-btn">
                <Send className="w-4 h-4 mr-1.5" /> Send delinquency notice
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Send delinquency notice · {detail.owner.name}</DialogTitle>
              </DialogHeader>
              <form onSubmit={sendNotice} className="space-y-4" data-testid="notice-form">
                <div>
                  <Label>Stage</Label>
                  <Select value={noticeForm.stage} onValueChange={(v) => setNoticeForm({ ...noticeForm, stage: v })}>
                    <SelectTrigger className="mt-1.5 h-11" data-testid="notice-stage-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {STAGE_META.map((s) => (
                        <SelectItem key={s.id} value={s.id}>{s.label} · cure in {s.days}d</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label>Late fee ($)</Label>
                    <Input type="number" step="0.01" value={noticeForm.late_fee} onChange={(e) => setNoticeForm({ ...noticeForm, late_fee: e.target.value })} className="mt-1.5 h-11" data-testid="notice-fee-input" />
                  </div>
                  <div>
                    <Label>Delivery</Label>
                    <Select value={noticeForm.method} onValueChange={(v) => setNoticeForm({ ...noticeForm, method: v })}>
                      <SelectTrigger className="mt-1.5 h-11" data-testid="notice-method-select"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="email">Email (auto-send)</SelectItem>
                        <SelectItem value="mail">Standard mail</SelectItem>
                        <SelectItem value="certified_mail">Certified mail</SelectItem>
                        <SelectItem value="hand_delivered">Hand delivered</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div>
                  <Label>Board notes (facts for AI Saint)</Label>
                  <Textarea value={noticeForm.notes} onChange={(e) => setNoticeForm({ ...noticeForm, notes: e.target.value })} className="mt-1.5 min-h-[80px]" placeholder="e.g. Prior email bounced — owner acknowledged debt at last meeting..." data-testid="notice-notes-input" />
                </div>
                <DialogFooter>
                  <Button type="submit" disabled={sending} className="rounded-full bg-gold text-secondary-foreground hover:brightness-110" data-testid="notice-submit-btn">
                    {sending ? "Generating…" : "Generate & send"}
                  </Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>

          <Tabs defaultValue="notices">
            <TabsList data-testid="delinq-tabs">
              <TabsTrigger value="notices" data-testid="tab-notices"><Mail className="w-3.5 h-3.5 mr-1" /> Notices ({detail.notices.length})</TabsTrigger>
              <TabsTrigger value="ledger" data-testid="tab-ledger"><Receipt className="w-3.5 h-3.5 mr-1" /> Ledger ({detail.entries.length})</TabsTrigger>
              <TabsTrigger value="comms" data-testid="tab-comms"><MessageSquare className="w-3.5 h-3.5 mr-1" /> Communications ({detail.communications.length})</TabsTrigger>
            </TabsList>
            <TabsContent value="notices" className="space-y-3 mt-4">
              {detail.notices.length === 0 && <div className="text-sm text-muted-foreground p-4">No notices yet.</div>}
              {detail.notices.map((n) => (
                <div key={n.id} className="hoa-card p-4" data-testid={`notice-${n.id}`}>
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="text-xs bg-primary/15 text-gold px-2 py-0.5 rounded-full font-medium">{n.stage_label}</span>
                        <span className="text-xs text-muted-foreground">{new Date(n.sent_at).toLocaleString()}</span>
                        <span className="text-xs text-muted-foreground">· via {n.method.replace(/_/g, " ")}</span>
                      </div>
                      <div className="text-sm font-mono text-gold">${n.balance_at_send.toFixed(2)} · cure {n.cure_days}d</div>
                      <div className="text-xs text-muted-foreground mt-1">Sent by {n.sent_by} {n.email_delivery?.status ? `· ${n.email_delivery.status}` : ""}</div>
                      <details className="mt-2 text-sm">
                        <summary className="cursor-pointer text-gold font-medium">Preview letter</summary>
                        <pre className="whitespace-pre-wrap font-serif text-foreground/85 mt-2 p-3 rounded bg-accent/30 border border-border text-[13px] leading-relaxed">{n.letter_body}</pre>
                      </details>
                    </div>
                    <div className="flex flex-col gap-2 min-w-[130px]">
                      <Button size="sm" variant="outline" className="rounded-full" onClick={() => openPrint(n.id)} data-testid={`print-notice-${n.id}`}>
                        <Printer className="w-3.5 h-3.5 mr-1" /> Print / PDF
                      </Button>
                      {n.task_completed ? (
                        <span className="text-xs text-gold flex items-center gap-1 justify-center">
                          <CheckCircle2 className="w-3.5 h-3.5" /> Completed
                        </span>
                      ) : (
                        <Button size="sm" className="rounded-full" onClick={() => completeTask(n.id)} data-testid={`complete-task-${n.id}`}>
                          Mark task done
                        </Button>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </TabsContent>
            <TabsContent value="ledger" className="mt-4">
              {detail.entries.length === 0 && <div className="text-sm text-muted-foreground p-4">No dues activity.</div>}
              <div className="hoa-card overflow-hidden">
                {detail.entries.map((e) => {
                  const isCharge = e.kind === "charge";
                  return (
                    <div key={e.id} className="grid grid-cols-12 gap-3 px-4 py-3 border-b border-border/50 items-center">
                      <div className="col-span-2 text-xs text-muted-foreground">{new Date(e.created_at).toLocaleDateString()}</div>
                      <div className="col-span-2">
                        <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${isCharge ? "bg-destructive/15 text-destructive" : "bg-[hsl(140_55%_45%/0.18)] text-[hsl(140_65%_45%)]"}`}>
                          {isCharge ? "charge" : "credit"}
                        </span>
                      </div>
                      <div className="col-span-5 text-sm">{e.description}</div>
                      <div className={`col-span-3 text-right font-mono font-semibold ${isCharge ? "text-destructive" : "text-[hsl(140_65%_45%)]"}`} data-testid={`ledger-amt-${e.id}`}>
                        {isCharge ? "+" : "−"}${e.amount.toFixed(2)}
                      </div>
                    </div>
                  );
                })}
              </div>
            </TabsContent>
            <TabsContent value="comms" className="space-y-2 mt-4">
              {detail.communications.length === 0 && <div className="text-sm text-muted-foreground p-4">No communications recorded.</div>}
              {detail.communications.map((c) => (
                <div key={c.id} className="hoa-card p-3 flex items-center gap-3" data-testid={`comm-${c.id}`}>
                  <div className="w-8 h-8 rounded-full bg-primary/15 flex items-center justify-center">
                    <Mail className="w-4 h-4 text-gold" />
                  </div>
                  <div className="flex-1">
                    <div className="text-sm font-medium">{c.subject}</div>
                    <div className="text-xs text-muted-foreground">{c.kind.replace(/_/g, " ")} · {new Date(c.at).toLocaleString()} · by {c.by}</div>
                  </div>
                  <div className="text-xs text-muted-foreground">{c.summary}</div>
                </div>
              ))}
            </TabsContent>
          </Tabs>

          {lastNotice && (
            <div className="mt-4 p-3 rounded-lg bg-primary/10 border border-primary/30 text-sm flex items-center gap-2">
              <Sparkles className="w-4 h-4 text-gold" />
              Last notice generated: <span className="font-semibold">{lastNotice.stage_label}</span> · <button className="text-gold underline" onClick={() => openPrint(lastNotice.id)}>Open printable copy</button>
            </div>
          )}
        </div>
      )}

      {!selected && (
        <Link to="/dues" className="text-sm text-muted-foreground hover:text-foreground inline-flex items-center gap-1">
          <DollarSign className="w-3.5 h-3.5" /> Post a new dues entry
        </Link>
      )}
    </div>
  );
}
